import React, {Dispatch, SetStateAction, useCallback} from 'react';

import {DateRangeInput} from 'src/components/input';
import {DraftStatus} from 'src/openapi/admin/models/DraftStatus';
import {SetUpStep} from 'src/openapi/admin/models/SetUpStep';

import ComboBox, {ComboboxItem, DefaultItem} from '@pb/kit-component-combo-box';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {FUNNEL_STEPS} from '../../statistics/step-funnel/step-funnel-data';
import {formatEnumValue} from '../../utils/format';

import type {SsoAccountsFilters} from '../../models/typedef';

type Props = {
    filter?: SsoAccountsFilters,
    onChange?: Dispatch<SetStateAction<SsoAccountsFilters>>,
};

const BOOLEAN_OPTIONS = [
    {value: 'true', caption: 'Yes'},
    {value: 'false', caption: 'No'},
];

const toBooleanValue = (value?: boolean) => {
    if (value === undefined) {
        return '';
    }

    return value ? 'true' : 'false';
};

export const SsoAccountsFiltersBody = ({filter, onChange}: Props) => {
    const setFilterValue = useCallback(<Key extends keyof SsoAccountsFilters>(
        key: Key,
        value?: SsoAccountsFilters[Key],
    ) => {
        if (!onChange) {
            return;
        }

        onChange(prev => {
            if (value === undefined || value === '') {
                const {[key]: _removed, ...rest} = prev;
                return rest;
            }

            return {...prev, [key]: value};
        });
    }, [onChange]);

    const setBooleanFilter = useCallback((key: 'ssoEnabled' | 'ssoMandatory' | 'hasErrors', value: string) => {
        setFilterValue(key, value ? value === 'true' : undefined);
    }, [setFilterValue]);

    return (
        <Group direction="v" gap="s">
            <Label>Status</Label>
            <div style={{width: 415}}>
                <ComboBox
                    value={filter?.status || ''}
                    onChange={value => setFilterValue('status', value as DraftStatus)}>
                    <DefaultItem value="" key="all" caption="All"/>
                    {Object.values(DraftStatus).map(status => (
                        <ComboboxItem key={status} value={status} caption={formatEnumValue(status)}/>
                    ))}
                </ComboBox>
            </div>

            <Label>Current Step</Label>
            <div style={{width: 415}}>
                <ComboBox
                    value={filter?.currentStep || ''}
                    onChange={value => setFilterValue('currentStep', value as SetUpStep)}>
                    <DefaultItem value="" key="all" caption="All"/>
                    {FUNNEL_STEPS.map(({step, label}) => (
                        <ComboboxItem key={step} value={step} caption={label}/>
                    ))}
                </ComboBox>
            </div>

            <Label>SSO Enabled</Label>
            <div style={{width: 415}}>
                <ComboBox
                    value={toBooleanValue(filter?.ssoEnabled)}
                    onChange={value => setBooleanFilter('ssoEnabled', value)}>
                    <DefaultItem value="" key="all" caption="All"/>
                    {BOOLEAN_OPTIONS.map(({value, caption}) => (
                        <ComboboxItem key={value} value={value} caption={caption}/>
                    ))}
                </ComboBox>
            </div>

            <Label>SSO Mandatory</Label>
            <div style={{width: 415}}>
                <ComboBox
                    value={toBooleanValue(filter?.ssoMandatory)}
                    onChange={value => setBooleanFilter('ssoMandatory', value)}>
                    <DefaultItem value="" key="all" caption="All"/>
                    {BOOLEAN_OPTIONS.map(({value, caption}) => (
                        <ComboboxItem key={value} value={value} caption={caption}/>
                    ))}
                </ComboBox>
            </div>
            <Label>Created</Label>
            <Group direction="h">
                <DateRangeInput
                    id="sso-accounts-created-from-picker"
                    width={200}
                    label="Starts"
                    clearable={true}
                    startDate={filter?.createdFrom}
                    endDate={filter?.createdTo}
                    preventFutureDate={true}
                    selectRangeType="start"
                    onChange={date => setFilterValue('createdFrom', date)}
                />
                <DateRangeInput
                    id="sso-accounts-created-to-picker"
                    width={200}
                    label={<>Ends&nbsp;&nbsp;</>}
                    clearable={true}
                    startDate={filter?.createdFrom}
                    endDate={filter?.createdTo}
                    preventFutureDate={true}
                    selectRangeType="end"
                    onChange={date => setFilterValue('createdTo', date)}
                />
            </Group>

            <Label>Finished</Label>
            <Group direction="h">
                <DateRangeInput
                    id="sso-accounts-finished-from-picker"
                    width={200}
                    label="Starts"
                    clearable={true}
                    startDate={filter?.finishedFrom}
                    endDate={filter?.finishedTo}
                    preventFutureDate={true}
                    selectRangeType="start"
                    onChange={date => setFilterValue('finishedFrom', date)}
                />
                <DateRangeInput
                    id="sso-accounts-finished-to-picker"
                    width={200}
                    label={<>Ends&nbsp;&nbsp;</>}
                    clearable={true}
                    startDate={filter?.finishedFrom}
                    endDate={filter?.finishedTo}
                    preventFutureDate={true}
                    selectRangeType="end"
                    onChange={date => setFilterValue('finishedTo', date)}
                />
            </Group>
        </Group>
    );
};
